import { useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { Easings } from "konva/lib/Tween";

import { GET_SEATING_CHART, GET_SEATING_CHART_VALIDATION } from "src/graphql/queries/seatingCharts";
import {
  UPDATE_SEATING_CHART,
  PUBLISH_SEATING_CHART,
  UPSERT_SEATING_CHART_SECTION,
  UPDATE_SEATING_CHART_SECTION_CANVAS,
  DELETE_SEATING_CHART_SECTION,
  UPSERT_SEATING_CHART_CATEGORY,
  DELETE_SEATING_CHART_CATEGORY,
  CREATE_SEATING_CHART_SEATS,
  UPDATE_SEATING_CHART_SEATS,
  DELETE_SEATING_CHART_SEATS,
} from "src/graphql/mutations/seatingCharts";
import { UPLOAD_MEDIA, SET_MEDIA_PUBLIC } from "src/graphql/mutations/media";
import { useMutationWithAuth, useQueryWithAuth } from "src/graphql/instances";

import { clearSelectTransform, handleReCenter, handleZoomIn, handleZoomOut } from "src/utils/canvasHandlers";
import { generateSeatsFromRowGroup, DEFAULT_SEAT_SPACING, DEFAULT_ROW_SPACING, nextTempId } from "src/utils/canvasSeatGeometryUtils";
import { handleImageFileChange } from "src/utils/canvasSeatHandlers";

export const SEAT_TOOLS = {
  SELECT: "select",
  TEXT: "text",
  DRAW: "draw",
  IMAGE: "image",
  SEAT: "seat",
  ROW: "row",
  GRID: "grid",
  SECTION: "section",
};

const useSeatingChartState = ({ chartId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading, refetch } = useQueryWithAuth(GET_SEATING_CHART, {
    variables: { id: chartId },
    fetchPolicy: "cache-and-network",
  });
  const { data: validationData, refetch: refetchValidation } = useQueryWithAuth(GET_SEATING_CHART_VALIDATION, {
    variables: { chartId },
    fetchPolicy: "network-only",
  });

  const [updateSeatingChart] = useMutationWithAuth(UPDATE_SEATING_CHART);
  const [publishSeatingChartMutation] = useMutationWithAuth(PUBLISH_SEATING_CHART);
  const [upsertSection] = useMutationWithAuth(UPSERT_SEATING_CHART_SECTION);
  const [deleteSection] = useMutationWithAuth(DELETE_SEATING_CHART_SECTION);
  const [upsertCategory] = useMutationWithAuth(UPSERT_SEATING_CHART_CATEGORY);
  const [deleteCategory] = useMutationWithAuth(DELETE_SEATING_CHART_CATEGORY);
  const [createSeats] = useMutationWithAuth(CREATE_SEATING_CHART_SEATS);
  const [updateSeats] = useMutationWithAuth(UPDATE_SEATING_CHART_SEATS);
  const [deleteSeats] = useMutationWithAuth(DELETE_SEATING_CHART_SEATS);
  const [uploadMedia] = useMutationWithAuth(UPLOAD_MEDIA);
  const [setMediaPublic] = useMutationWithAuth(SET_MEDIA_PUBLIC);

  // -- working state (local until Save) --------------------------------------
  const [chart, setChart] = useState(null);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [seats, setSeats] = useState([]);
  const [chartCanvasElements, setChartCanvasElements] = useState([]);

  const originalRef = useRef({ seats: [], sections: [], chartCanvasElements: [], sectionCanvasElements: {} });

  const [focusedSectionId, setFocusedSectionId] = useState(null);
  const [tool, setTool] = useState(SEAT_TOOLS.SELECT);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedCategoryForNewSeats, setSelectedCategoryForNewSeats] = useState(null);

  // interactive drag state per tool
  const [marquee, setMarquee] = useState(null);
  const [ghostPosition, setGhostPosition] = useState(null);
  const [hoverGuide, setHoverGuide] = useState({ guideX: null, guideY: null });
  const [rowDraft, setRowDraft] = useState(null);
  const [gridDraft, setGridDraft] = useState(null);
  const [sectionDraft, setSectionDraft] = useState(null);
  const [drawingRect, setDrawingRect] = useState(null);

  const [seatSpacing, setSeatSpacing] = useState(DEFAULT_SEAT_SPACING);
  const [rowSpacing, setRowSpacing] = useState(DEFAULT_ROW_SPACING);

  const [isSaving, setIsSaving] = useState(false);

  const shapeRefs = useRef({});
  const transformerRef = useRef(null);
  const stageRef = useRef(null);
  const layerRef = useRef(null);
  const fileInputRef = useRef(null);
  const hydratedForChartIdRef = useRef(null);

  const applyServerChartData = (c) => {
    if (!c) return;
    setChart({
      id: c.id,
      name: c.name,
      mode: c.mode,
      status: c.status,
      canvasWidth: c.canvasWidth,
      canvasHeight: c.canvasHeight,
    });
    setSections(c.sections ?? []);
    setCategories(c.categories ?? []);
    setSeats(c.seats ?? []);
    setChartCanvasElements(c.canvasElements ?? []);
    originalRef.current = {
      seats: c.seats ?? [],
      sections: c.sections ?? [],
      chartCanvasElements: c.canvasElements ?? [],
    };
  };


  // useEffect(() => {
  //   if (!data?.seatingChart) return;
  //   const c = data.seatingChart;
  //   setChart({
  //     id: c.id,
  //     name: c.name,
  //     mode: c.mode,
  //     status: c.status,
  //     canvasWidth: c.canvasWidth,
  //     canvasHeight: c.canvasHeight,
  //   });
  //   setSections(c.sections ?? []);
  //   setCategories(c.categories ?? []);
  //   setSeats(c.seats ?? []);
  //   setChartCanvasElements(c.canvasElements ?? []);

  //   originalRef.current = {
  //     seats: c.seats ?? [],
  //     sections: c.sections ?? [],
  //     chartCanvasElements: c.canvasElements ?? [],
  //   };
  // }, [data]);

  useEffect(() => {
    if (!data?.seatingChart) return;
    if (hydratedForChartIdRef.current === chartId) return;
    applyServerChartData(data.seatingChart);
    hydratedForChartIdRef.current = chartId;
  }, [data, chartId]);

  // clear any in-progress tool drag whenever the active tool changes, so
  // switching tools mid-drag can't leave a stale preview on screen
  useEffect(() => {
    setMarquee(null);
    setGhostPosition(null);
    setHoverGuide({ guideX: null, guideY: null });
    setRowDraft(null);
    setGridDraft(null);
    setDrawingRect(null);
  }, [tool]);

  const isSectioned = chart?.mode === "SECTIONED";
  const seatToolsUsable = !isSectioned || !!focusedSectionId;

  const visibleSeats = seats;
  const visibleCanvasElements = chartCanvasElements;
  const setVisibleCanvasElements = (updater) =>
    setChartCanvasElements((prev) => (typeof updater === "function" ? updater(prev) : updater));

  const focusSection = (section) => {
    clearSelectTransform(setSelectedIds, transformerRef);
    setFocusedSectionId(section.id);

    const stage = stageRef.current;
    if (!stage) return;
    const padding = 100;
    const scale = Math.min(
      (stage.width() - padding) / Math.max(section.width, 1),
      (stage.height() - padding) / Math.max(section.height, 1),
    );
    stage.to({
      x: stage.width() / 2 - (section.x + section.width / 2) * scale,
      y: stage.height() / 2 - (section.y + section.height / 2) * scale,
      scaleX: scale,
      scaleY: scale,
      duration: 0.4,
      easing: Easings.EaseInOut,
    });
  };

  const unfocusSection = () => {
    setFocusedSectionId(null);
    handleReCenter(stageRef);
  };

  // -- seat placement -------------------------------------------------------------
  const addSeat = (seat) => {
    setSeats((prev) => [
      ...prev,
      { ...seat, sectionId: seat.sectionId ?? focusedSectionId, categoryId: seat.categoryId ?? selectedCategoryForNewSeats },
    ]);
  };

  const finalizeRowGroup = (descriptor, generatedSeats) => {
    setSeats((prev) => [...prev, ...generatedSeats]);
  };

  const finalizeGridGroup = (rows) => {
    setSeats((prev) => [...prev, ...rows.flatMap((r) => r.seats)]);
  };

  const deleteSelectedSeats = () => {
    const idsToDelete = new Set(selectedIds);
    const affectedGroupIds = new Set(
      seats.filter((s) => idsToDelete.has(s.id) && s.metadata?.rowGroup).map((s) => s.metadata.rowGroup.rowGroupId),
    );

    setSeats((prev) => {
      let next = prev.filter((s) => !idsToDelete.has(s.id));

      for (const groupId of affectedGroupIds) {
        const remaining = next
          .filter((s) => s.metadata?.rowGroup?.rowGroupId === groupId)
          .sort((a, b) => Number(a.metadata.positionKey.split("-")[1]) - Number(b.metadata.positionKey.split("-")[1]));

        if (!remaining.length) continue; // entire row deleted — nothing to renumber

        const descriptor = remaining[0].metadata.rowGroup;

        // split into contiguous runs by physical column index
        const runs = [[remaining[0]]];
        for (let k = 1; k < remaining.length; k++) {
          const prevCol = Number(runs[runs.length - 1].at(-1).metadata.positionKey.split("-")[1]);
          const col = Number(remaining[k].metadata.positionKey.split("-")[1]);
          (col === prevCol + 1 ? runs[runs.length - 1] : (runs.push([]), runs[runs.length - 1])).push(remaining[k]);
        }

        next = next.filter((s) => s.metadata?.rowGroup?.rowGroupId !== groupId);

        runs.forEach((run) => {
          const newGroupId = runs.length === 1 ? groupId : nextTempId("rowgroup");
          const anchorSeat = run[0];
          // Labels are NEVER recomputed here — a surviving seat keeps its
          // existing label exactly as-is. We only rebuild each run's own
          // descriptor bookkeeping so a FUTURE "seats per row" edit on either
          // half regenerates the right thing without touching the other half.
          const newDescriptor = {
            ...descriptor,
            rowGroupId: newGroupId,
            anchor: { x: anchorSeat.x, y: anchorSeat.y },
            seatsPerRow: run.length,
            startSeatNumber: Number(anchorSeat.label),
          };
          run.forEach((seat, i) => {
            next.push({ ...seat, metadata: { rowGroup: newDescriptor, positionKey: `0-${i}` } });
          });
        });
      }

      return next;
    });

    setSelectedIds([]);
  };

  const assignCategoryToSelection = (categoryId) => {
    setSeats((prev) => prev.map((s) => (selectedIds.includes(s.id) ? { ...s, categoryId } : s)));
  };

  const updateRowGroup = (rowGroupId, patch) => {
    const groupSeats = seats.filter((s) => s.metadata?.rowGroup?.rowGroupId === rowGroupId);
    if (!groupSeats.length) return;

    const baseDescriptor = groupSeats[0].metadata.rowGroup;
    const anchorSeat = groupSeats.find((s) => s.metadata?.positionKey === "0-0") ?? groupSeats[0];
    const anchor = patch.anchor ?? { x: anchorSeat.x, y: anchorSeat.y };

    const descriptor = {
      ...baseDescriptor,
      sectionId: groupSeats[0].sectionId,
      categoryId: groupSeats[0].categoryId ?? baseDescriptor.categoryId,
      ...patch,
      anchor,
    };
    const regenerated = generateSeatsFromRowGroup(descriptor, groupSeats);
    setSeats((prev) => [...prev.filter((s) => s.metadata?.rowGroup?.rowGroupId !== rowGroupId), ...regenerated]);
  };

  // -- decorative elements ----------------------------------------------------
  const addTextElement = (textShape) => setVisibleCanvasElements((prev) => [...prev, textShape]);
  const addRectElement = (rect) => setVisibleCanvasElements((prev) => [...prev, rect]);
  const addImageElement = (image) => setVisibleCanvasElements((prev) => [...prev, image]);

  const handleImageInputChange = (e) => handleImageFileChange(e, enqueueSnackbar, uploadMedia, setMediaPublic, addImageElement);

  // -- sections (overview canvas) --------------------------------------------------
  const finalizeNewSection = (rect) => {
    setSections((prev) => [
      ...prev,
      { id: `section-${Date.now()}`, chartId, name: `Section ${prev.length + 1}`, orderIndex: prev.length, ...rect, rotation: 0, seatCount: 0 },
    ]);
    setTool(SEAT_TOOLS.SELECT);
  };

  const renameSection = (id, name) => setSections((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));

  const moveSectionBy = (id, dx, dy) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, x: s.x + dx, y: s.y + dy } : s)));
    setSeats((prev) => prev.map((s) => (s.sectionId === id ? { ...s, x: s.x + dx, y: s.y + dy } : s)));
  };

  const updateSectionTransform = (id, patch) => {
    const current = sections.find((s) => s.id === id);
    const dx = current && patch.x !== undefined ? patch.x - current.x : 0;
    const dy = current && patch.y !== undefined ? patch.y - current.y : 0;

    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

    // keep contained seats locked to the section whenever its origin moves —
    // width/height-only changes (resizing in place) should NOT drag seats,
    // only an actual x/y shift should
    if (dx || dy) {
      setSeats((prev) => prev.map((s) => (s.sectionId === id ? { ...s, x: s.x + dx, y: s.y + dy } : s)));
    }
  };

  const deleteSectionLocally = async (id) => {
    const isPersisted = originalRef.current.sections.some((s) => s.id === id);

    if (isPersisted) {
      try {
        await deleteSection({ variables: { id } });
      } catch (e) {
        enqueueSnackbar(e.message ?? "Failed to delete section", { variant: "error" });
        return;
      }
    }

    setSections((prev) => prev.filter((s) => s.id !== id));
    setSeats((prev) => prev.filter((s) => s.sectionId !== id));
    if (focusedSectionId === id) unfocusSection();
  };

  // -- categories (saved immediately) ------------------------------------------
  const saveCategory = async (category) => {
    const { data: res } = await upsertCategory({ variables: { input: { chartId, ...category } } });
    const saved = res.upsertSeatingChartCategory;
    setCategories((prev) => {
      const exists = prev.some((c) => c.id === saved.id);
      return exists ? prev.map((c) => (c.id === saved.id ? saved : c)) : [...prev, saved];
    });
    return saved;
  };

  const removeCategory = async (id) => {
    try {
      await deleteCategory({ variables: { id } });
      setCategories((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const deleteSelection = () => {
    if (!selectedIds.length) return;

    const seatIds = selectedIds.filter((id) => seats.some((s) => s.id === id));
    const elementIds = selectedIds.filter((id) => chartCanvasElements.some((el) => el.id === id));
    const sectionIds = selectedIds.filter((id) => sections.some((s) => s.id === id));

    if (seatIds.length) setSeats((prev) => prev.filter((s) => !seatIds.includes(s.id)));
    if (elementIds.length) setChartCanvasElements((prev) => prev.filter((el) => !elementIds.includes(el.id)));
    sectionIds.forEach((id) => deleteSectionLocally(id));

    clearSelectTransform(setSelectedIds, transformerRef);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      // don't hijack Delete/Backspace while typing anywhere — the section
      // rename field, the row-group spacing inputs, or the floating text
      // editor (appended directly to document.body, so it won't be caught
      // by a React-level check) would otherwise lose characters AND delete
      // the selected shape at the same time
      const active = document.activeElement;
      const isTyping = active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable);
      if (isTyping || document.querySelector("textarea")) return;

      deleteSelection();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIds, seats, chartCanvasElements, sections]);

  // -- save / publish -------------------------------------------------------------
  const diffSeats = () => {
    const original = originalRef.current.seats;
    const originalIds = new Set(original.map((s) => s.id));
    const currentIds = new Set(seats.map((s) => s.id));

    const toCreate = seats.filter((s) => !originalIds.has(s.id));
    const toDelete = original.filter((s) => !currentIds.has(s.id)).map((s) => s.id);
    const toUpdate = seats.filter((s) => {
      if (!originalIds.has(s.id)) return false;
      const before = original.find((o) => o.id === s.id);
      return JSON.stringify(before) !== JSON.stringify(s);
    });

    return { toCreate, toUpdate, toDelete };
  };

  const saveChart = async () => {
    setIsSaving(true);
    try {
      // 1. Save sections FIRST, and capture the real backend id for any
      //    brand-new section. Seats created in the same save cycle may still
      //    be pointing at that section's local temp id (e.g. "section-171...")
      //    — createSeatingChartSeats will reject that as a bad foreign key,
      //    so we need the mapping before we touch seats at all.
      const originalSections = originalRef.current.sections;
      const sectionIdMap = new Map(); // localTempId -> realId
      let workingSections = sections;

      for (const section of sections) {
        const before = originalSections.find((s) => s.id === section.id);
        const changed =
          !before ||
          before.x !== section.x ||
          before.y !== section.y ||
          before.width !== section.width ||
          before.height !== section.height ||
          before.rotation !== section.rotation ||
          before.name !== section.name;

        if (!changed) continue;

        const { data } = await upsertSection({
          variables: {
            input: {
              id: before ? section.id : undefined,
              chartId,
              name: section.name,
              orderIndex: section.orderIndex,
              x: section.x,
              y: section.y,
              width: section.width,
              height: section.height,
              rotation: section.rotation,
            },
          },
        });

        const realId = data.upsertSeatingChartSection.id;
        if (!before && realId !== section.id) {
          sectionIdMap.set(section.id, realId);
        }
        workingSections = workingSections.map((s) => (s.id === section.id ? { ...s, id: realId } : s));
      }

      // 2. Remap any seats that reference a section's local temp id to the
      //    real id we just got back, and push both updated collections into
      //    state so the UI (and the NEXT save) is working off real ids too.
      const workingSeats = sectionIdMap.size
        ? seats.map((s) => (sectionIdMap.has(s.sectionId) ? { ...s, sectionId: sectionIdMap.get(s.sectionId) } : s))
        : seats;

      if (sectionIdMap.size) {
        setSections(workingSections);
        setSeats(workingSeats);
      }

      // 3. Only now diff and save seats, against the remapped set.
      const original = originalRef.current.seats;
      const originalIds = new Set(original.map((s) => s.id));
      const currentIds = new Set(workingSeats.map((s) => s.id));

      const toCreate = workingSeats.filter((s) => !originalIds.has(s.id));
      const toDelete = original.filter((s) => !currentIds.has(s.id)).map((s) => s.id);
      const toUpdate = workingSeats.filter((s) => {
        if (!originalIds.has(s.id)) return false;
        const before = original.find((o) => o.id === s.id);
        return JSON.stringify(before) !== JSON.stringify(s);
      });

      if (toCreate.length) {
        await createSeats({ variables: { input: { chartId, seats: toCreate.map(stripLocalOnlyFields) } } });
      }
      if (toUpdate.length) {
        await updateSeats({ variables: { chartId, seats: toUpdate.map(stripLocalOnlyFields) } });
      }
      if (toDelete.length) {
        await deleteSeats({ variables: { chartId, ids: toDelete } });
      }

      if (JSON.stringify(chartCanvasElements) !== JSON.stringify(originalRef.current.chartCanvasElements)) {
        await updateSeatingChart({ variables: { input: { id: chartId, canvasElements: chartCanvasElements } } });
      }

      const { data: refreshed } = await refetch();
      await refetchValidation();
      applyServerChartData(refreshed?.seatingChart);
      enqueueSnackbar("Chart saved");
    } catch (e) {
      enqueueSnackbar(e.message ?? "Failed to save chart", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const publishChart = async () => {
    try {
      await saveChart();
      const result = await publishSeatingChartMutation({ variables: { id: chartId } });
      const { data: refreshed } = await refetch();
      applyServerChartData(refreshed?.seatingChart);
      enqueueSnackbar(`Chart published (v${result.data.publishSeatingChart.version})`);
    } catch (e) {
      enqueueSnackbar(e.message ?? "Failed to publish chart", { variant: "error" });
    }
  };

  const updateSeatPosition = (id, { x, y }) =>
    setSeats((prev) => prev.map((s) => (s.id === id ? { ...s, x, y } : s)));

  const updateSeatTransform = (id, patch) =>
    setSeats((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const updateSeatRotation = (id, rotation) =>
    setSeats((prev) => prev.map((s) => (s.id === id ? { ...s, rotation } : s)));

  const updateElementPosition = (id, { x, y }) =>
    setVisibleCanvasElements((prev) => prev.map((el) => (el.id === id ? { ...el, x, y } : el)));

  const updateSectionPosition = (id, { x, y }) =>
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, x, y } : s)));

  const updateElementTransform = (id, patch) =>
    setVisibleCanvasElements((prev) => prev.map((el) => (el.id === id ? { ...el, ...patch } : el)));

  return {
    loading,
    chart,
    sections,
    categories,
    seats,
    visibleSeats,
    visibleCanvasElements,
    setVisibleCanvasElements,
    isSectioned,
    seatToolsUsable,
    focusedSectionId,
    focusSection,
    unfocusSection,

    tool,
    setTool,
    selectedIds,
    setSelectedIds,
    selectedCategoryForNewSeats,
    setSelectedCategoryForNewSeats,

    updateSeatPosition,
    updateSeatRotation,
    updateSeatTransform,
    updateElementPosition,
    updateSectionPosition,
    updateElementTransform,

    marquee,
    setMarquee,
    ghostPosition,
    setGhostPosition,
    hoverGuide,
    setHoverGuide,
    rowDraft,
    setRowDraft,
    gridDraft,
    setGridDraft,
    sectionDraft,
    setSectionDraft,
    drawingRect,
    setDrawingRect,
    seatSpacing,
    setSeatSpacing,
    rowSpacing,
    setRowSpacing,

    addSeat,
    finalizeRowGroup,
    finalizeGridGroup,
    deleteSelectedSeats,
    deleteSelection,
    assignCategoryToSelection,
    updateRowGroup,

    addTextElement,
    addRectElement,
    handleImageInputChange,

    finalizeNewSection,
    renameSection,
    moveSectionBy,
    updateSectionTransform,
    deleteSectionLocally,

    saveCategory,
    removeCategory,

    validationIssues: validationData?.seatingChartValidation ?? [],
    refetchValidation,

    isSaving,
    saveChart,
    publishChart,

    shapeRefs,
    transformerRef,
    stageRef,
    layerRef,
    fileInputRef,
    handleReCenter: () => handleReCenter(stageRef),
    handleZoomIn: () => handleZoomIn(stageRef),
    handleZoomOut: () => handleZoomOut(stageRef),
  };
};

function stripLocalOnlyFields(seat) {
  const isPersisted = typeof seat.id === "string" && !/^(seat|rowgroup)-/.test(seat.id);
  return {
    id: isPersisted ? seat.id : undefined,
    sectionId: seat.sectionId ?? null,
    categoryId: seat.categoryId ?? null,
    rowLabel: seat.rowLabel ?? null,
    label: seat.label,
    x: seat.x,
    y: seat.y,
    rotation: seat.rotation ?? 0,
    metadata: seat.metadata ?? undefined,
  };
}

export default useSeatingChartState;